import { useState } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import FlatPicker from "react-flatpickr";
import moment from "moment";
import Flight, { getDefaultDateForFlightDateTimePicker } from "../../../models/flight";
import { getDisplayableTextFromFlightPlanPoint } from "../../../models/flightPlan";
import { useAppDispatch } from "../../../store/hooks";
import { duplicateFlight } from "../../../store/flightSlice";
import { useNavigate } from "react-router-dom";

interface DuplicateFlightModalProps {
    flight: Flight;
    onClose: () => void;
}

function DuplicateFlightModal({ flight, onClose }: DuplicateFlightModalProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const defaultDeparture = getDefaultDateForFlightDateTimePicker();
    const [departureDateTime, setDepartureDateTime] = useState<Date>(defaultDeparture);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const departureName = flight.route
        ? flight.route.departure.name
        : flight.customRoute
        ? getDisplayableTextFromFlightPlanPoint(flight.customRoute.departure)
        : "";

    const destinationName = flight.route
        ? flight.route.destination.name
        : flight.customRoute
        ? getDisplayableTextFromFlightPlanPoint(flight.customRoute.destination)
        : "";

    const routeName = flight.route ? flight.route.name : "Custom";

    const utcOffset = moment(departureDateTime).utcOffset();
    const pickerDate = moment(departureDateTime).subtract(utcOffset, "minutes").toDate();
    //Compose entirely in the display frame and convert once. Taking the date from the shifted frame and
    //the time from the unshifted one rolls the date whenever the edit crosses midnight.
    const fromDisplayFrame = (displayDate: Date) => moment(displayDate).add(utcOffset, "minutes").toDate();

    const handleCreate = async () => {
        setIsCreating(true);
        setError(undefined);
        try {
            const newFlight = await dispatch(
                duplicateFlight({
                    flightId: flight.id!,
                    departureDateTime,
                })
            ).unwrap();
            onClose();
            navigate(`/flights/${newFlight.id}`);
        } catch (e: any) {
            setError(typeof e === "string" ? e : e?.message ?? "Failed to duplicate flight.");
            setIsCreating(false);
        }
    };

    return (
        <Modal show onHide={onClose}>
            <Modal.Header>
                <p className="h5">Duplicate Flight</p>
                <Button variant="close" onClick={onClose} />
            </Modal.Header>
            <Modal.Body>
                <Row className="gy-3">
                    <Col xs={12}>
                        <strong>Route:</strong> {departureName} &rarr; {destinationName}
                    </Col>
                    <Col xs={12}>
                        <strong>Using:</strong> {routeName}
                    </Col>
                    <Col xs={12}>
                        <FlatPicker
                            value={pickerDate}
                            onClose={(s) => {
                                if (s.length > 0) {
                                    setDepartureDateTime(
                                        fromDisplayFrame(
                                            new Date(
                                                s[0].getFullYear(),
                                                s[0].getMonth(),
                                                s[0].getDate(),
                                                pickerDate.getHours(),
                                                pickerDate.getMinutes()
                                            )
                                        )
                                    );
                                }
                            }}
                            options={{
                                dateFormat: "d-m-Y",
                                static: true,
                            }}
                            className="form-control bg-white"
                            render={({ value, ...props }, ref) => {
                                return (
                                    <div className="input-container rounded-lg" style={{ backgroundColor: "#F7F7F7" }}>
                                        <input ref={ref} className="w-100 bg-transparent" style={{ cursor: "pointer" }} readOnly />
                                        <label className={value ? "filled" : ""}>Departure Date (UTC)</label>
                                    </div>
                                );
                            }}
                        />
                    </Col>
                    <Col xs={12}>
                        <FlatPicker
                            value={pickerDate}
                            onClose={(s) => {
                                if (s.length > 0) {
                                    setDepartureDateTime(
                                        fromDisplayFrame(
                                            new Date(
                                                pickerDate.getFullYear(),
                                                pickerDate.getMonth(),
                                                pickerDate.getDate(),
                                                s[0].getHours(),
                                                s[0].getMinutes()
                                            )
                                        )
                                    );
                                }
                            }}
                            options={{
                                enableTime: true,
                                noCalendar: true,
                                dateFormat: "H:i",
                                time_24hr: true,
                                minuteIncrement: 5,
                                static: true,
                            }}
                            className="form-control bg-white"
                            render={({ value, ...props }, ref) => {
                                return (
                                    <div className="input-container rounded-lg" style={{ backgroundColor: "#F7F7F7" }}>
                                        <input ref={ref} className="w-100 bg-transparent" style={{ cursor: "pointer" }} readOnly />
                                        <label className={value ? "filled" : ""}>Departure Time (UTC)</label>
                                    </div>
                                );
                            }}
                        />
                    </Col>
                    {error && (
                        <Col xs={12}>
                            <small className="text-danger">{error}</small>
                        </Col>
                    )}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button variant="dark" onClick={handleCreate} disabled={isCreating}>
                    {isCreating ? <Spinner animation="border" size="sm" /> : "Create"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default DuplicateFlightModal;
